import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { cloneMap, isSameData, isSameMap, mapToData } from '../../dto/data-model-utils.js';
import { DailyTicketSalesStats } from './daily-ticket-sales-stats.js';

export class DailyTicketStats extends DataModel {
  static readonly TYPE_ID: string = '3dbd2ce2a19a42f4b3d368f2e2d011c3';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly ticketsSalesPerEvent: Map<string, DailyTicketSalesStats>,
  ) {
    super();
  }

  get $typeId(): string {
    return DailyTicketStats.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(DailyTicketStats.SCHEMA_VERSION);
  }

  static empty(): DailyTicketStats {
    return new DailyTicketStats(
        new Map<string, DailyTicketSalesStats>(),
    );
  }

  static fromMap(
      context: SerializationContext,
      map: SerializedData,
  ): DailyTicketStats {
    return new DailyTicketStats(
        MapUtils.getMap<string, DailyTicketSalesStats>(map, 'ticketsSalesPerEvent', (k, v) => {
          return { key: k as string, value: context.fromData(v as SerializedData) };
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      ticketsSalesPerEvent: mapToData(this.ticketsSalesPerEvent, (k, v) => {
        return { key: k, value: context.toData(v) };
      }),
    };
  }

  clone(): this {
    return new DailyTicketStats(
        cloneMap(this.ticketsSalesPerEvent, (v) => v.clone()),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameMap(other.ticketsSalesPerEvent, this.ticketsSalesPerEvent, (a, b) => isSameData(a, b));
  }
}

export class DailyTicketStatsBuilder extends DataModelBuilder<DailyTicketStats> {
  build(context: SerializationContext, map: SerializedData): DailyTicketStats {
    return DailyTicketStats.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(DailyTicketStats.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(DailyTicketStats.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version parameter `ticketsSoldPerEvent` of type `Map<string, number>`
    // was replaced with parameter `ticketsSalesPerEvent` of type  `Map<string, DailyTicketSalesStats>`.
    if(version < 2) {
      map['ticketsSalesPerEvent'] ??= mapToData(
          MapUtils.getMap<string, DailyTicketSalesStats>(
              map,
              'ticketsSoldPerEvent',
              (k, v) => {
                return { key: k as string, value: DailyTicketSalesStats.legacy(Number(v)) };
              }
          ),
          (k, v) => {
            return { key: k, value: context.toData(v) };
          },
      );

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
